import React, { useState } from 'react';
import {
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { insertDocument } from '../../../../utility/firebaseHelpers';
import { serverTimestamp } from 'firebase/firestore';
import { Message } from '../../types';

interface ReportMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: Message | null;
  conversationId: string;
  currentUserId?: string;
}

const REPORT_CATEGORIES = [
  { value: 'harassment', label: 'Harassment or Bullying' },
  { value: 'scam', label: 'Scam or Fraud Attempt' },
  { value: 'spam', label: 'Spam or Unwanted Contact' },
  { value: 'inappropriate', label: 'Inappropriate Content' },
  { value: 'threatening', label: 'Threatening Behavior' },
  { value: 'discrimination', label: 'Discrimination' },
  { value: 'other', label: 'Other' }
];

const ReportMessageModal: React.FC<ReportMessageModalProps> = ({
  isOpen,
  onClose,
  message,
  conversationId,
  currentUserId
}) => {
  const [reportCategory, setReportCategory] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!reportCategory) {
      setSubmitError('Please select a reason for reporting.');
      return;
    }

    if (!reportDetails.trim() || reportDetails.trim().length < 10) {
      setSubmitError('Please provide more details about your report (minimum 10 characters).');
      return;
    }

    if (!message) {
      setSubmitError('No message selected to report.');
      return;
    }

    setIsSubmitting(true);

    try {
      const reportData = {
        messageId: message.id,
        conversationId,
        messageContent: message.content,
        messageSenderId: message.senderId,
        messageTimestamp: message.timestamp,
        reporterId: currentUserId || 'anonymous',
        reportCategory,
        reportDetails,
        timestamp: serverTimestamp(),
        status: 'pending',
        priority: 3 // Default priority: 1 (highest) to 5 (lowest), 3 = medium
      };

      await insertDocument('messageReports', reportData);

      setSubmitSuccess(true);

      setTimeout(() => {
        handleClose();
      }, 2500);
    } catch (error) {
      console.error('Error submitting message report:', error);
      setSubmitError('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReportCategory('');
    setReportDetails('');
    setSubmitError(null);
    setSubmitSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Report Message
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Success Message */}
        {submitSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Report Submitted
            </h3>
            <p className="text-gray-600">
              Thank you for reporting this message. Our team will review it and take appropriate action.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Message Preview */}
            {message && (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Reported Message:</p>
                <p className="text-gray-800 text-sm italic line-clamp-3">
                  "{message.content}"
                </p>
              </div>
            )}

            {/* Report Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Report <span className="text-red-500">*</span>
              </label>
              <select
                value={reportCategory}
                onChange={(e) => setReportCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                disabled={isSubmitting}
                required
              >
                <option value="">Select a reason...</option>
                {REPORT_CATEGORIES.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Report Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Details <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Please explain why you are reporting this message..."
                disabled={isSubmitting}
                required
              />
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3" />
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  isSubmitting
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Submit Report'
                )}
              </button>
            </div>

            {/* Privacy Notice */}
            <p className="text-xs text-gray-500 text-center pt-2 border-t border-gray-200">
              Reports are reviewed by our moderation team. False reports may result in account restrictions.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportMessageModal;
